import React, { useEffect, useRef, useState } from 'react';
import { X, Wand2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MAX_NAME_LENGTH = 60;

/**
 * VoiceVariationModal — cria/edita uma variação de voz (mesma voz do catálogo
 * salva em "Minhas Vozes" com nome customizado escolhido pelo usuário).
 *
 * Props:
 *   - mode: 'create' | 'edit'
 *   - originalName: nome original da voz (exibido embaixo do input)
 *   - initialName: nome pré-preenchido (edit) ou '' (create)
 *   - onSave(name): async — consumer faz o POST/PUT; modal mostra "salvando"
 *   - onClose()
 */
export default function VoiceVariationModal({
  mode = 'create',
  originalName = '',
  initialName = '',
  onSave,
  onClose,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // autoFocus + seleciona o texto no edit (troca rápida do nome inteiro)
    if (inputRef.current) {
      inputRef.current.focus();
      if (initialName) inputRef.current.select();
    }
  }, [initialName]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !saving && trimmed !== (mode === 'edit' ? initialName.trim() : null);

  const handleSave = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
    }
  };

  const isEdit = mode === 'edit';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--dp-bg-card, #1a1a2e)',
          border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
          borderRadius: '16px',
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--dp-font)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px 10px',
          borderBottom: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))',
        }}>
          <h3 style={{
            margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            {isEdit
              ? <Pencil size={15} style={{ color: 'var(--dp-blue-500)' }} />
              : <Wand2 size={15} style={{ color: 'var(--dp-blue-500)' }} />}
            {isEdit ? t('audio.variation_edit_title') : t('audio.variation_create_title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: 'var(--dp-text-tertiary)',
              cursor: 'pointer', padding: '4px', display: 'flex', borderRadius: '6px',
            }}
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px 18px' }}>
          <label style={{
            display: 'block', fontSize: '11px', fontWeight: 500,
            color: 'var(--dp-text-secondary)', marginBottom: '6px',
          }}>
            {t('audio.variation_name_label')}
          </label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') onClose();
            }}
            placeholder={t('audio.variation_name_placeholder')}
            style={{
              width: '100%',
              padding: '9px 12px',
              background: 'var(--dp-bg-input, var(--dp-bg-primary))',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: 'var(--dp-radius-sm, 8px)',
              color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)',
              fontSize: '13px',
              outline: 'none',
              transition: 'all 0.3s',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500)'; e.target.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.25)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
          />

          {originalName && (
            <div style={{
              marginTop: '8px', fontSize: '11px', color: 'var(--dp-text-tertiary)',
              display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0,
            }}>
              <span style={{ flexShrink: 0 }}>{t('audio.variation_original_voice')}:</span>
              <span style={{
                color: 'var(--dp-text-secondary)', fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{originalName}</span>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px',
                borderRadius: '9999px',
                border: '1px solid var(--dp-border-subtle)',
                background: 'transparent',
                color: 'var(--dp-text-secondary)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--dp-font)',
                transition: 'all 0.2s',
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '8px 18px',
                borderRadius: '9999px',
                border: '1px solid transparent',
                background: canSave
                  ? 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'
                  : 'var(--dp-border-subtle)',
                color: canSave ? '#FFFFFF' : 'var(--dp-text-tertiary)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--dp-font)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { if (canSave) e.currentTarget.style.background = 'linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%)'; }}
              onMouseLeave={(e) => { if (canSave) e.currentTarget.style.background = 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'; }}
            >
              {saving ? t('audio.variation_saving') : t('audio.variation_save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
